import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as api from "../api";
import { toast } from "react-toastify";
import { computeCalendarRange } from "../../utils/calendarRangeUtils";
import { filterJobsForTenant, jobBelongsToTenant } from "../../utils/tenantUtils";

/** Jedan HTTP GET po ključu dok traje prethodni (više dispatch-eva deli isti Promise). */
const getJobsInflightByKey = new Map();
/** Poslednje uspešno učitavanje — sprečava lavinu GET-ova (npr. više job_updated u istoj sekundi). */
const getJobsLastSuccessAtByKey = new Map();
const GET_JOBS_MIN_INTERVAL_MS = 450;

const syncLoadingFlag = (state) => {
  state.loading = state.jobsFetching || state.jobSaving;
};

const archiveFetchInflightByKey = new Map();

function archiveFetchKey(arg) {
  const bt = arg?.businessType || "";
  const a = arg?.assignedToFilter || "all";
  if (arg?.fetchAll) return `${bt}|fetchAll|${a}`;
  return `${bt}|${arg?.startDate}|${arg?.endDate}|${a}`;
}

/** Normalizuje argument getJobs (string = ceo skup, objekat = prozor ili fetchAll). */
export function normalizeGetJobsArg(arg) {
  if (arg == null || arg === "") {
    return { fetchAll: true };
  }
  if (typeof arg === "string") {
    return { businessType: arg, fetchAll: true };
  }
  if (arg.fetchAll) {
    return { fetchAll: true, businessType: arg.businessType };
  }
  return {
    fetchAll: false,
    businessType: arg.businessType,
    startDate: arg.startDate,
    endDate: arg.endDate,
    includeUnscheduled: arg.includeUnscheduled !== false,
  };
}

function getJobsCacheKey(norm) {
  if (norm.fetchAll) {
    return `all:${norm.businessType == null || norm.businessType === "" ? "__default__" : String(norm.businessType)}`;
  }
  return `win:${norm.businessType || ""}|${norm.startDate}|${norm.endDate}|${norm.includeUnscheduled !== false}`;
}

// Async thunks
export const createJob = createAsyncThunk(
  "job/createJob",
  async ({ jobData }, { rejectWithValue }) => {
    try {
      const response = await api.createJob(jobData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Network error' });
    }
  }
);

export const getJobs = createAsyncThunk(
  "job/getJobs",
  async (arg, { rejectWithValue, getState }) => {
    const norm = normalizeGetJobsArg(arg);
    const key = getJobsCacheKey(norm);
    const existing = getJobsInflightByKey.get(key);
    if (existing) return existing;

    const lastOk = getJobsLastSuccessAtByKey.get(key);
    const now = Date.now();
    if (lastOk != null && now - lastOk < GET_JOBS_MIN_INTERVAL_MS) {
      const cached = getState().job?.jobs;
      if (Array.isArray(cached)) return cached;
    }

    const promise = (async () => {
      try {
        const apiArg = norm.fetchAll
          ? norm.businessType != null && norm.businessType !== ""
            ? norm.businessType
            : { fetchAll: true }
          : norm;
        const response = await api.getJobs(apiArg);
        getJobsLastSuccessAtByKey.set(key, Date.now());
        return response.data;
      } catch (error) {
        getJobsLastSuccessAtByKey.delete(key);
        return rejectWithValue(error.response?.data || { message: 'Network error' });
      } finally {
        if (getJobsInflightByKey.get(key) === promise) {
          getJobsInflightByKey.delete(key);
        }
      }
    })();

    getJobsInflightByKey.set(key, promise);
    return promise;
  }
);

/** Ponavlja poslednji getJobs (isti prozor ili ceo skup) — nakon CRUD / osvežavanja. */
export const refreshJobs = createAsyncThunk(
  "job/refreshJobs",
  async (_, { dispatch, getState }) => {
    const cfg = getState().job?.lastFetchConfig;
    if (cfg && (cfg.fetchAll || (cfg.startDate && cfg.endDate))) {
      return dispatch(getJobs(cfg)).unwrap();
    }
    let bt;
    try {
      const p = typeof localStorage !== "undefined" ? localStorage.getItem("profile") : null;
      if (p) bt = JSON.parse(p)?.result?.businessType;
    } catch (e) {
      /* ignore */
    }
    const { startDate, endDate } = computeCalendarRange(new Date(), 'week');
    return dispatch(getJobs({
      businessType: bt,
      startDate,
      endDate,
      includeUnscheduled: true,
    })).unwrap();
  }
);

/** Arhiva: samo završeni poslovi u datom opsegu (ne meša se sa aktivnim jobs u store-u). */
export const fetchArchiveJobs = createAsyncThunk(
  "job/fetchArchiveJobs",
  async (arg, { rejectWithValue }) => {
    const key = archiveFetchKey(arg);
    const existing = archiveFetchInflightByKey.get(key);
    if (existing) return existing;

    const promise = (async () => {
      try {
        const response = await api.getJobs({
          businessType: arg.businessType,
          archiveOnly: true,
          ...(arg.fetchAll
            ? { fetchAll: true }
            : { startDate: arg.startDate, endDate: arg.endDate }),
          ...(arg.assignedToFilter ? { assignedToFilter: arg.assignedToFilter } : {}),
        });
        return response.data;
      } catch (error) {
        return rejectWithValue(error.response?.data || { message: "Network error" });
      } finally {
        if (archiveFetchInflightByKey.get(key) === promise) {
          archiveFetchInflightByKey.delete(key);
        }
      }
    })();

    archiveFetchInflightByKey.set(key, promise);
    return promise;
  }
);

export const refreshArchiveJobs = createAsyncThunk(
  "job/refreshArchiveJobs",
  async (_, { dispatch, getState }) => {
    const cfg = getState().job?.archiveLastFetchConfig;
    if (cfg?.fetchAll && cfg?.businessType) {
      return dispatch(fetchArchiveJobs(cfg)).unwrap();
    }
    if (cfg?.startDate && cfg?.endDate && cfg?.businessType) {
      return dispatch(fetchArchiveJobs(cfg)).unwrap();
    }
    let bt;
    try {
      const p = typeof localStorage !== "undefined" ? localStorage.getItem("profile") : null;
      if (p) bt = JSON.parse(p)?.result?.businessType;
    } catch (e) {
      /* ignore */
    }
    const { startDate, endDate } = computeCalendarRange(new Date(), "week");
    return dispatch(
      fetchArchiveJobs({ businessType: bt, startDate, endDate })
    ).unwrap();
  }
);

export const getJob = createAsyncThunk(
  "job/getJob",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.getJob(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateJob = createAsyncThunk(
  "job/updateJob",
  async ({ id, jobData }, { rejectWithValue }) => {
    try {
      if (!id) return rejectWithValue({ message: 'Job ID is required' });
      const response = await api.updateJob({ id, jobData });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to update job' });
    }
  }
);

export const deleteJob = createAsyncThunk(
  "job/deleteJob",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.deleteJob(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// No more mock data - using real API

const initialJobState = {
  jobs: [],
  currentJob: null,
  loading: false,
  jobsFetching: false,
  jobSaving: false,
  error: "",
  lastFetchConfig: null,
  archiveJobs: [],
  archiveLoading: false,
  archiveLastFetchConfig: null,
};

const jobSlice = createSlice({
  name: "job",
  initialState: initialJobState,
  reducers: {
    resetJobState: () => ({ ...initialJobState }),
    setCurrentJob: (state, action) => {
      const jobId = action.payload;
      state.currentJob =
        state.jobs.find((job) => job._id === jobId) ||
        state.archiveJobs.find((job) => job._id === jobId) ||
        null;
    },
    clearCurrentJob: (state) => {
      state.currentJob = null;
    },
    // Optimized: Update single job in array without full refresh (for real-time WebSocket updates)
    removeJobById: (state, action) => {
      const jobId = action.payload;
      if (!jobId) return;
      state.jobs = state.jobs.filter((job) => String(job._id) !== String(jobId));
      state.archiveJobs = state.archiveJobs.filter((job) => String(job._id) !== String(jobId));
      if (state.currentJob && String(state.currentJob._id) === String(jobId)) {
        state.currentJob = null;
      }
    },
    updateJobInArray: (state, action) => {
      const { jobId, updates, fullJob } = action.payload;
      const id = String(jobId);
      let jobIndex = state.jobs.findIndex((job) => String(job._id) === id);

      const mergeUpdates = (base) => {
        const updatedJob = { ...base, ...updates };
        if ('travelStartTime' in updates) updatedJob.travelStartTime = updates.travelStartTime;
        if ('workStartTime' in updates) updatedJob.workStartTime = updates.workStartTime;
        return updatedJob;
      };

      if (jobIndex === -1 && fullJob && String(fullJob._id) === id) {
        if (jobBelongsToTenant(fullJob)) {
          state.jobs.push(mergeUpdates(fullJob));
        }
      } else if (jobIndex !== -1) {
        const updatedJob = mergeUpdates(state.jobs[jobIndex]);
        if (!jobBelongsToTenant(updatedJob)) {
          state.jobs.splice(jobIndex, 1);
          if (state.currentJob && String(state.currentJob._id) === id) {
            state.currentJob = null;
          }
        } else {
          state.jobs[jobIndex] = updatedJob;
          if (state.currentJob && String(state.currentJob._id) === id) {
            state.currentJob = { ...state.currentJob, ...updatedJob };
          }
        }
      }

      const archIdx = state.archiveJobs.findIndex((j) => String(j._id) === id);
      if (archIdx !== -1) {
        const merged = { ...state.archiveJobs[archIdx], ...updates };
        if ("travelStartTime" in updates) merged.travelStartTime = updates.travelStartTime;
        if ("workStartTime" in updates) merged.workStartTime = updates.workStartTime;
        state.archiveJobs[archIdx] = merged;
      }
    }
  },
  extraReducers: (builder) => {
    // Create job
    builder.addCase(createJob.pending, (state) => {
      state.jobSaving = true;
      syncLoadingFlag(state);
      state.error = "";
    });
    builder.addCase(createJob.fulfilled, (state, action) => {
      state.jobSaving = false;
      syncLoadingFlag(state);
      if (jobBelongsToTenant(action.payload)) {
        state.jobs.push(action.payload);
      }
    });
    builder.addCase(createJob.rejected, (state, action) => {
      state.jobSaving = false;
      syncLoadingFlag(state);
      state.error = action.payload?.message || "An error occurred";
      toast.error(action.payload?.message || "Došlo je do greške");
    });
    
    // Get all jobs
    builder.addCase(getJobs.pending, (state) => {
      state.jobsFetching = true;
      syncLoadingFlag(state);
      state.error = "";
    });
    builder.addCase(getJobs.fulfilled, (state, action) => {
      state.jobsFetching = false;
      syncLoadingFlag(state);
      state.jobs = filterJobsForTenant(action.payload);
      state.lastFetchConfig = normalizeGetJobsArg(action.meta.arg);
    });
    builder.addCase(getJobs.rejected, (state, action) => {
      state.jobsFetching = false;
      syncLoadingFlag(state);
      state.error = action.payload?.message || "An error occurred";
    });

    builder.addCase(fetchArchiveJobs.pending, (state) => {
      state.archiveLoading = true;
      state.error = "";
    });
    builder.addCase(fetchArchiveJobs.fulfilled, (state, action) => {
      state.archiveLoading = false;
      state.archiveJobs = Array.isArray(action.payload) ? action.payload : [];
      state.archiveLastFetchConfig = action.meta.arg || null;
    });
    builder.addCase(fetchArchiveJobs.rejected, (state, action) => {
      state.archiveLoading = false;
      state.error = action.payload?.message || "An error occurred";
    });
    
    // Get single job
    builder.addCase(getJob.pending, (state) => {
      state.jobsFetching = true;
      syncLoadingFlag(state);
      state.error = "";
    });
    builder.addCase(getJob.fulfilled, (state, action) => {
      state.jobsFetching = false;
      syncLoadingFlag(state);
      state.currentJob = action.payload;
    });
    builder.addCase(getJob.rejected, (state, action) => {
      state.jobsFetching = false;
      syncLoadingFlag(state);
      state.error = action.payload?.message || "An error occurred";
    });
    
    // Update job
    builder.addCase(updateJob.pending, (state) => {
      state.jobSaving = true;
      syncLoadingFlag(state);
      state.error = "";
    });
    builder.addCase(updateJob.fulfilled, (state, action) => {
      state.jobSaving = false;
      syncLoadingFlag(state);
      const updatedJobIndex = state.jobs.findIndex(
        (job) => job._id === action.payload._id
      );
      if (updatedJobIndex !== -1) {
        state.jobs[updatedJobIndex] = action.payload;
      }
      const archI = state.archiveJobs.findIndex((j) => j._id === action.payload._id);
      if (archI !== -1) {
        state.archiveJobs[archI] = action.payload;
      } else if (action.payload?.status === 'Archived') {
        state.archiveJobs = [action.payload, ...state.archiveJobs];
      }
      state.currentJob = action.payload;
    });
    builder.addCase(updateJob.rejected, (state, action) => {
      state.jobSaving = false;
      syncLoadingFlag(state);
      state.error = action.payload?.message || "An error occurred";
      toast.error(action.payload?.message || "Došlo je do greške");
    });
    
    // Delete job
    builder.addCase(deleteJob.pending, (state) => {
      state.jobSaving = true;
      syncLoadingFlag(state);
      state.error = "";
    });
    builder.addCase(deleteJob.fulfilled, (state, action) => {
      state.jobSaving = false;
      syncLoadingFlag(state);
      state.jobs = state.jobs.filter((job) => job._id !== action.meta.arg);
      state.archiveJobs = state.archiveJobs.filter((job) => job._id !== action.meta.arg);
      if (state.currentJob && state.currentJob._id === action.meta.arg) {
        state.currentJob = null;
      }
    });
    builder.addCase(deleteJob.rejected, (state, action) => {
      state.jobSaving = false;
      syncLoadingFlag(state);
      state.error = action.payload?.message || "An error occurred";
      toast.error(action.payload?.message || "Došlo je do greške");
    });

    builder.addMatcher(
      (action) => action.type === 'auth/setLogout' || action.type === 'auth/login/fulfilled',
      () => ({ ...initialJobState })
    );
  },
});

export const { setCurrentJob, clearCurrentJob, updateJobInArray, removeJobById, resetJobState } = jobSlice.actions;

export default jobSlice.reducer; 